import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, yieldToMainThread } from '@theme/utilities';

/** @typedef {{ items: Element[], mobileSlides: Element[], nextUrl: string, pagination: Element | null, hasSentinel: boolean }} PageData */

const ROOT_MARGIN = '600px';

const SELECTORS = {
  list: '[data-featured-blog-list]',
  grid: '[data-featured-blog-grid]',
  mobileGrid: '[data-featured-blog-mobile-grid]',
  item: ':scope > .resource-list__item',
  mobileSlide: 'slideshow-slide',
  mobileScroller: '[ref="scroller"]',
  sentinel: '[data-featured-blog-sentinel]',
  pagination: '#FeaturedBlogPagination',
};

/**
 * Infinite scroll for featured blog posts using the Section Rendering API.
 */
class FeaturedBlogPostsList extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #loading = false;

  /** @type {Map<string, PageData>} */
  #pageCache = new Map();

  connectedCallback() {
    if (Shopify?.designMode) return;
    if (this.hasAttribute('data-initialized')) return;

    const sentinel = this.querySelector(SELECTORS.sentinel);
    const wrapper = this.querySelector(SELECTORS.list);

    if (!sentinel || !wrapper?.dataset.nextUrl) return;

    this.setAttribute('data-initialized', '');

    this.#prefetch(wrapper.dataset.nextUrl);

    this.#observer = new IntersectionObserver((entries) => this.#handleIntersect(entries), {
      rootMargin: ROOT_MARGIN,
      threshold: 0,
    });

    this.#observer.observe(sentinel);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
    this.#pageCache.clear();
  }

  /**
   * @param {string} url
   */
  #prefetch(url) {
    if (!url || this.#pageCache.has(url)) return;

    requestIdleCallback(
      () => {
        this.#loadPage(url).catch(() => {});
      },
      { timeout: 2000 }
    );
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  async #handleIntersect(entries) {
    const entry = entries[0];
    if (!entry?.isIntersecting || this.#loading) return;

    const wrapper = this.querySelector(SELECTORS.list);
    const nextUrl = wrapper?.dataset.nextUrl;

    if (!nextUrl) return;

    this.#loading = true;
    this.classList.add('is-loading');

    try {
      const pageData = await this.#loadPage(nextUrl);
      this.#pageCache.delete(nextUrl);

      this.#appendPage(pageData);
      this.#updatePagination(pageData);
      this.#updateSentinel(pageData);

      if (pageData.nextUrl) {
        wrapper.dataset.nextUrl = pageData.nextUrl;
        history.pushState({}, '', nextUrl);
        this.#prefetch(pageData.nextUrl);
      } else {
        wrapper.removeAttribute('data-next-url');
      }

      await yieldToMainThread();
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Featured blog infinite scroll error:', error);
      }
    } finally {
      this.#loading = false;
      this.classList.remove('is-loading');
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<PageData>}
   */
  async #loadPage(url) {
    if (this.#pageCache.has(url)) {
      return /** @type {PageData} */ (this.#pageCache.get(url));
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) throw new Error('Missing data-section-id');

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const sectionUrl = new URL(url, window.location.origin);
    const html = await sectionRenderer.getSectionHTML(
      sectionId,
      true,
      sectionUrl,
      this.#abortController.signal
    );

    const pageData = this.#parsePage(html);
    this.#pageCache.set(url, pageData);

    return pageData;
  }

  /**
   * @param {string} html
   * @returns {PageData}
   */
  #parsePage(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceList = parsed.querySelector(SELECTORS.list);
    const sourceGrid = parsed.querySelector(SELECTORS.grid);

    return {
      items: sourceGrid ? [...sourceGrid.querySelectorAll(SELECTORS.item)] : [],
      mobileSlides: [...parsed.querySelectorAll(`${SELECTORS.mobileGrid} ${SELECTORS.mobileSlide}`)],
      nextUrl: sourceList?.dataset.nextUrl || '',
      pagination: parsed.querySelector(SELECTORS.pagination),
      hasSentinel: Boolean(parsed.querySelector(SELECTORS.sentinel)),
    };
  }

  /**
   * @param {PageData} pageData
   */
  #appendPage(pageData) {
    const grid = this.querySelector(SELECTORS.grid);

    if (grid && pageData.items.length) {
      const fragment = document.createDocumentFragment();

      for (const item of pageData.items) {
        fragment.appendChild(document.importNode(item, true));
      }

      grid.appendChild(fragment);
    }

    this.#appendMobileSlides(pageData.mobileSlides);
  }

  /**
   * @param {Element[]} slides
   */
  #appendMobileSlides(slides) {
    if (!slides.length) return;

    const scroller = this.querySelector(`${SELECTORS.mobileGrid} ${SELECTORS.mobileScroller}`);
    if (!scroller) return;

    const fragment = document.createDocumentFragment();

    for (const slide of slides) {
      fragment.appendChild(document.importNode(slide, true));
    }

    scroller.appendChild(fragment);
  }

  /**
   * @param {PageData} pageData
   */
  #updatePagination(pageData) {
    const currentPagination = this.querySelector(SELECTORS.pagination);

    if (pageData.pagination && currentPagination) {
      currentPagination.innerHTML = pageData.pagination.innerHTML;
    } else if (currentPagination) {
      currentPagination.remove();
    }
  }

  /**
   * @param {PageData} pageData
   */
  #updateSentinel(pageData) {
    const sentinel = this.querySelector(SELECTORS.sentinel);
    if (!sentinel) return;

    this.#observer?.unobserve(sentinel);

    if (pageData.hasSentinel && pageData.nextUrl) {
      this.#observer?.observe(sentinel);
      return;
    }

    sentinel.remove();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('featured-blog-posts-list')) {
  customElements.define('featured-blog-posts-list', FeaturedBlogPostsList);
}
